import { PayloadAction } from '@reduxjs/toolkit'
import { RequestStatus } from 'features/common'
import { all, call, cancelled, debounce, put, select, takeEvery } from 'redux-saga/effects'
import {
  addToIgnoredProcessingJobs,
  checkProcessingJob,
  createProcessingJob,
  deleteProcessingJob,
  getProcessingJobsList,
  setIgnoredProcessingJobs
} from '../api/processingJobs'
import {
  ProcessingJob,
  ProcessingJobResponse,
  JobStatusType,
  JobType,
  ProcessingJobPayload,
  ResponseStatus,
  JobErrorCode,
  ImportingJobPayload,
  ProcessingType,
  JobsPopperTab,
  ProcessingJobStatus
} from '../types'
import {
  addJob,
  checkProcessingJobStatusRequest,
  createProcessingJobRequest,
  getProcessingJobsRequest,
  getTableProcessingJobsRequest,
  setCreateProcessingJobStatus,
  addProcessingJobInProgress,
  removeProcessingJobInProgress,
  setProcessingJobs,
  setProcessingJobsStatus,
  setTableProcessingJobs,
  setTableProcessingJobsStatus,
  updateJob,
  deleteProcessingJobRequest,
  removeFromProcessingJobs,
  clearProcessingJobsRequest,
  removeProcessingJobs,
  setCurrentProcessingJob,
  setJobsPopperState
} from './jobsProcessingSlice'
import { CARTO_IGNORED_PROCESSING_JOBS } from '../types'
import { getLocalStorageItem } from 'app/api/localStorage'
import { updateDataExplorer } from 'features/data-explorer/state/dataExplorerSagas'
import { getMetricsSagasFunction } from 'features/metrics/utils'
import { pollJobUntilFinished } from './jobsSagas'

function* createProcessingJobAndPolling(
  action: PayloadAction<{ connection: string; processingJobPayload: ProcessingJobPayload }>
) {
  try {
    yield put(setCreateProcessingJobStatus(RequestStatus.Pending))
    const processingType = action.payload.processingJobPayload.metadata?.processingType
    if (processingType === ProcessingType.OptimizeTable) {
      yield put(setJobsPopperState({ open: true, tab: JobsPopperTab.Processing }))
    }
    yield call(setProcessingJob, action)
  } catch (e) {
    yield put(setCreateProcessingJobStatus(RequestStatus.Rejected))
  }
}

function* setProcessingJob(
  action: PayloadAction<{ connection: string; processingJobPayload: ProcessingJobPayload }>,
  error?: { status: string; error: string }
) {
  try {
    const data: ProcessingJob = yield call(createProcessingJob, action.payload)
    yield put(addProcessingJobInProgress(data.jobId))
    yield put(setCurrentProcessingJob(data))
    if (error) {
      data.dryRun = error
      data.status = {
        jobId: data.jobId,
        status: JobStatusType.Failure,
        error: {
          msg: error.error,
          code: error.status
        }
      }
    }
    yield put(addJob(data))
    yield put(checkProcessingJobStatusRequest([data]))
    yield put(setCreateProcessingJobStatus(RequestStatus.Fulfilled))
    yield put(setCreateProcessingJobStatus(RequestStatus.Idle))
  } catch (e) {
    console.error('setProcessingJob failed', e)
    yield put(setCreateProcessingJobStatus(RequestStatus.Rejected))
  }
}

export function processingJobFilter(job) {
  const jobs = getLocalStorageItem(CARTO_IGNORED_PROCESSING_JOBS)
  if (jobs?.length) {
    return !jobs.some((j) => j.jobId === job.jobId)
  }
  return !(job?.status?.status === JobStatusType.Failure && job.status?.error?.code === JobErrorCode.Stopped)
}

export function exportingJobFilter(job) {
  const jobs = getLocalStorageItem(CARTO_IGNORED_PROCESSING_JOBS)
  if (jobs?.length) {
    return !jobs.some((j) => j.jobId === job.jobId)
  }
  return !(job?.status?.status === JobStatusType.Failure && job.status?.error?.code === JobErrorCode.Stopped)
}

function* clearProcessingJobs(action: PayloadAction<ProcessingJob[]>) {
  try {
    yield put(setProcessingJobsStatus(RequestStatus.Pending))
    yield call(setIgnoredProcessingJobs, { jobs: action.payload })
    yield put(removeProcessingJobs(action.payload))
    yield put(setProcessingJobsStatus(RequestStatus.Fulfilled))
    yield put(setCurrentProcessingJob())
  } catch (e) {
    console.error(e)
    yield put(setProcessingJobsStatus(RequestStatus.Rejected))
  }
}

function* getProcessingJobs(action: PayloadAction<ImportingJobPayload>) {
  try {
    yield put(setProcessingJobsStatus(RequestStatus.Pending))

    const processingJobs: ProcessingJobResponse[] = yield call(getProcessingJobsList, action.payload)
    const filteredProcessingJobs: ProcessingJob[] = processingJobs
      .filter((job) => job.metadata.type === JobType.Processing)
      .filter((job) => job.metadata.connection)
      .filter(processingJobFilter)
      .map((job) => {
        return { jobId: job.externalId, connection: job.metadata.connection, response: job }
      })

    yield put(setProcessingJobs(filteredProcessingJobs))
    yield put(setProcessingJobsStatus(RequestStatus.Fulfilled))
    const jobsToBeChecked = filteredProcessingJobs.filter(
      (job) => !job.status || job.status.status === JobStatusType.Pending || job.status.status === JobStatusType.Running
    )
    yield put(checkProcessingJobStatusRequest(jobsToBeChecked))
  } catch (e) {
    console.error(e)
  }
}

function* getTableProcessingJobs(action: PayloadAction<ImportingJobPayload & { tableId: string }>) {
  try {
    yield put(setTableProcessingJobsStatus(RequestStatus.Pending))

    const processingJobs: ProcessingJobResponse[] = yield call(getProcessingJobsList, action.payload)

    const tableProcessingJobs: ProcessingJob[] = processingJobs
      .filter((job) => job.metadata.optimizedTableId === action.payload?.tableId)
      .map((job) => {
        return { jobId: job.externalId, connection: job.metadata.connection, response: job }
      })

    try {
      const jobsStatus = yield all(
        tableProcessingJobs.map(
          yield (job) => {
            return call(checkProcessingJob, { connection: job.connection as string, jobId: job.jobId })
          }
        )
      )

      const tableProcessingJobsWithStatut = tableProcessingJobs.map((job) => {
        return { ...job, status: jobsStatus.find((x) => x.jobId === job.jobId) }
      })

      yield put(setTableProcessingJobs(tableProcessingJobsWithStatut))
      yield put(setTableProcessingJobsStatus(RequestStatus.Fulfilled))
    } catch (e) {
      console.error(e)
    }
  } catch (e) {
    console.error(e)
  }
}

function* pollingAllProcessingJobsStatus(action: PayloadAction<ProcessingJob[]>) {
  const jobs = action.payload
  try {
    yield all(
      jobs.map(
        yield (job) => {
          return call(pollingProcessingJobStatus, job)
        }
      )
    )
  } catch (e) {
    console.error(e)
  }
}

function* pollingProcessingJobStatus(job: ProcessingJob) {
  try {
    const jobStatus: ProcessingJobStatus = yield pollJobUntilFinished(() =>
      checkProcessingJob({
        connection: job.connection as string,
        jobId: job.jobId
      })
    )

    const currentProcessingJob: ProcessingJob | undefined = yield select((state) => state.jobs.currentProcessingJob)
    const processingJobs: string[] = yield select((state) => state.jobs.processingJobsInProgress)

    if (jobStatus?.status === JobStatusType.Success && processingJobs.includes(job.jobId)) {
      yield put(removeProcessingJobInProgress(job.jobId))
      yield call(updateDataExplorer, job)

      if (currentProcessingJob) {
        yield put(setCurrentProcessingJob({ ...job, status: jobStatus }))
      }
      // if current job finished and its ok
      if (currentProcessingJob?.jobId === job.jobId) {
        yield call(sendMetrics, { job, statistics: jobStatus.provider?.statistics, isCompleted: 'yes' })
      }
    }
    if (jobStatus.status === JobStatusType.Failure && jobStatus.error?.code === JobErrorCode.Stopped) {
      yield put(removeFromProcessingJobs(job.jobId))
      yield put(setCurrentProcessingJob())
    } else {
      // if current job finished with error
      if (jobStatus.status === JobStatusType.Failure && currentProcessingJob?.jobId === job.jobId) {
        yield call(sendMetrics, { job, statistics: jobStatus.provider?.statistics, isCompleted: 'no' })
      }
      yield put(updateJob(jobStatus))
      if (currentProcessingJob) {
        yield put(setCurrentProcessingJob({ ...job, status: jobStatus }))
      }
    }
  } catch (e) {
    console.error('pollingProcessingJobStatus', e)
  }
}

function* cancelProcessingJob(action: PayloadAction<{ job: ProcessingJob }>) {
  try {
    const data: { status: string } = yield call(deleteProcessingJob, {
      jobId: action.payload.job.jobId,
      connection: action.payload.job.connection as string
    })
    const job = action.payload.job
    if (data.status === ResponseStatus.Ok) {
      yield call(addToIgnoredProcessingJobs, job)
      yield put(removeProcessingJobInProgress(job.jobId))
      yield put(removeFromProcessingJobs(job.jobId))
      yield put(setCurrentProcessingJob())
      yield call(sendMetrics, { job, statistics: null, isCompleted: 'cancelled' })
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    throw Error(e)
  }
}

function* watchFetchJobs() {
  yield takeEvery(deleteProcessingJobRequest.type, cancelProcessingJob)
  yield takeEvery(createProcessingJobRequest.type, createProcessingJobAndPolling)
  yield takeEvery(getProcessingJobsRequest.type, getProcessingJobs)
  yield takeEvery(getTableProcessingJobsRequest.type, getTableProcessingJobs)
  yield takeEvery(clearProcessingJobsRequest.type, clearProcessingJobs)

  try {
    yield debounce(1000, checkProcessingJobStatusRequest.type, pollingAllProcessingJobsStatus)
  } catch (e) {
    // TODO
  }
}

function* sendMetrics({ job, statistics, isCompleted }) {
  try {
    if (job.response.metadata.event) {
      const metricsFn = getMetricsSagasFunction(job.response.metadata.event.name)
      if (metricsFn) {
        yield put(metricsFn({ job: job.response.metadata, statistics, isCompleted }))
      }
    }
  } catch (e) {
    console.error(e)
  }
}

export default function* jobsSaga() {
  try {
    yield all([watchFetchJobs()])
  } catch (e) {
    //TODO
  } finally {
    if (yield cancelled()) {
      // console.log('jobsSaga#cancelled')
    }
  }
}
